import { computed, ref, watch } from 'vue'

/**
 * GARDE ANTI-PERTE DE SAISIE.
 *
 * Répond à une seule question : « ce formulaire a-t-il bougé depuis son
 * ouverture ? » — celle dont `DialogForm` a besoin pour décider si Échap et la
 * croix ferment la fenêtre ou demandent confirmation.
 *
 * La comparaison se fait sur une empreinte JSON de l'objet de formulaire, ce qui
 * couvre le cas qui compte : l'utilisateur tape, puis revient à la valeur de
 * départ. Il n'a alors plus rien à perdre, et la fenêtre se referme sans
 * l'interroger — un « êtes-vous sûr ? » qui se déclenche sur une saisie annulée
 * apprend surtout à cliquer sans lire.
 *
 * Limite assumée : les valeurs non sérialisables (fichiers, dates natives) ne
 * sont pas vues. Une fenêtre qui en manipule passe `dirty` à la main.
 *
 * @param {import('vue').Ref<object>} form  le ref réactif du formulaire
 * @param {object} [options]
 * @param {boolean} [options.immediate=true] prend l'empreinte dès l'appel
 * @returns {{ dirty: import('vue').ComputedRef<boolean>, baseline: Function, reset: Function }}
 */
export function useDialogGuard(form, options = {}) {
  const { immediate = true } = options

  const snapshot = ref(immediate ? fingerprint(form.value) : null)

  /** Prend l'état courant (ou celui fourni) comme nouveau point de référence. */
  const baseline = (value) => {
    snapshot.value = fingerprint(value === undefined ? form.value : value)
  }

  /** Remet le formulaire à la valeur fournie ET repose le point de référence. */
  const reset = (value) => {
    if (value !== undefined) form.value = value
    baseline(form.value)
  }

  const dirty = computed(() => {
    if (snapshot.value === null) return false
    return fingerprint(form.value) !== snapshot.value
  })

  return { dirty, baseline, reset }
}

/**
 * Rejoue la garde à chaque ouverture d'une fenêtre : le point de référence est
 * repris quand `open` passe à vrai, pour qu'une fenêtre rouverte ne parte pas
 * déjà « modifiée ».
 */
export function useDialogGuardOn(open, form, options = {}) {
  const guard = useDialogGuard(form, { ...options, immediate: false })

  watch(
    open,
    (isOpen) => {
      if (isOpen) guard.baseline()
    },
    { immediate: true }
  )

  return guard
}

function fingerprint(value) {
  try {
    return JSON.stringify(value)
  } catch {
    // Objet circulaire ou non sérialisable : on préfère ne pas garder du tout
    // plutôt que de garder à tort et bloquer chaque fermeture.
    return null
  }
}
